import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { IFlowCore, IFlowSubscriber } from '../../../modules/epflow'
import { Locale } from '../../../modules/locale'
import MainModule from '../../../modules/main'

Locale.getInstance().registerKeySet('fr', 'create-contributor-recap', {
  title: 'Résumé du contributeur',
  description: `Vous y êtes presque ! Pour modifier votre contributeur, cliquez sur «Modifier». Sinon, cliquez sur «Soumettre» afin qu'il soit validé par l'équipe de La Vitrine.`,
  submitSuccess: 'Contributor soumise avec succès!',
})

Locale.getInstance().registerKeySet('en', 'create-contributor-recap', {
  title: 'Contributor Summary',
  description: `You're almost there! If you want to modify your submission, click "Back" to return to the screen that needs changes. When you're finished, click "Submit" to send everything for validation by La Vitrine's internal teams.`,
  submitSuccess: 'Contributor submitted successfully!',
})

// Contributor types with FR/EN labels
// The id (value) is the French text as expected by the database
const CONTRIBUTOR_TYPES = [
  { id: 'Person', name: { fr: 'Personne', en: 'Person' } },
  { id: 'Organization', name: { fr: 'Organisation', en: 'Organization' } },
]

export interface LvcCreateContributorRecap {
  flowGroup: string
  flowStep: string
}

export class LvcCreateContributorRecap
  extends MinzeElement
  implements MinzeElement, IFlowSubscriber
{
  private flowCore: IFlowCore | undefined

  attrs: Attrs = [
    ['flow-step', ''],
    ['flow-group', ''],
  ]

  reactive: Reactive = [
    ['nameEn', '', false],
    ['nameFr', '', false],
    ['contributorType', '', false],
    ['imageUrl', '', false],
  ]

  onReady() {
    const mainModule = MainModule.getInstance()
    mainModule?.getFlowManager()?.subscribe(this)
    this.flowCore = MainModule.getInstance().getFlowManager().getCore(this.flowGroup)
    this.rerender()
  }

  onDestroy() {
    MainModule?.getInstance()?.getFlowManager()?.unsubscribe(this)
  }

  public async deserialize(stepData: any, data: any): Promise<void> {
    // createContributorInfo
    // createContributorAddImage
    const contributorData = data.createContributorInfo || {}
    const imageData = data.createContributorAddImage || {}
    this.nameEn = contributorData.nameEn || ''
    this.nameFr = contributorData.nameFr || ''
    this.contributorType = contributorData.contributorType || ''
    // Convert Firebase storage path to downloadable URL
    if (imageData.imageUrl) {
      this.imageUrl = await MainModule.getInstance().getAuth().getImageUrl(imageData.imageUrl)
    } else {
      this.imageUrl = ''
    }
    this.rerender()
  }

  public async serialize(): Promise<unknown> {
    // Data formatting before moving to next step, which is submission
    return {
      nameEn: this.nameEn,
      nameFr: this.nameFr,
      contributorType: this.contributorType,
      imageUrl: this.imageUrl,
    }
  }

  public async validate(): Promise<boolean> {
    return true
  }

  onSubmitClick() {
    // Placeholder - show alert for now
    alert(Locale.getKey('create-contribution-recap.submitSuccess'))
    // TODO: Implement actual submission with confirmation popup
  }

  getContributorTypeLabel() {
    if (!this.contributorType) {
      return ''
    }

    const selectedContributorType = CONTRIBUTOR_TYPES.find(
      contributorType => contributorType.id === this.contributorType
    )

    if (selectedContributorType) {
      return selectedContributorType.name[Locale.language as 'fr' | 'en']
    }

    return this.contributorType
  }

  html = () => `
    <lvc-box mt="24px" textAlign="left">
      <lvc-text font-preset="titleMobileSmall">
        ${Locale.getKey('create-contribution-recap.title')}
      </lvc-text>
    </lvc-box>
    <lvc-box mt="24px" textAlign="left">
      <lvc-text font-preset="medium">
        ${Locale.getKey('create-contribution-recap.description')}
      </lvc-text>
    </lvc-box>
    
    <!-- Contributor Card -->
    <lvc-box
      mt="24px"
      background="#FFF"
      borderRadius="8px"
      overflow="hidden"
      border="1px solid var(--nuit-30, rgba(30, 0, 125, 0.30))"
    >
      ${
        this.imageUrl
          ? /*html*/ `
        <lvc-box
          width="100%"
          aspect-ratio="16/9"
          overflow="hidden"
        >
          <img
            src="${this.imageUrl}"
            alt="${Locale.language === 'fr' ? this.nameFr : this.nameEn}"
            style="width: 100%; height: 100%; object-fit: cover; display: block;"
          />
        </lvc-box>
      `
          : ''
      }
      <lvc-box padding="16px">
        <lvc-text font-preset="smallExtraBold" font-weight="800" text-transform="uppercase">
          ${Locale.language === 'fr' ? this.nameFr : this.nameEn}
        </lvc-text>
        <lvc-box mt="8px">
          <lvc-text font-preset="small">
            ${this.getContributorTypeLabel()}
          </lvc-text>
        </lvc-box>
      </lvc-box>
    </lvc-box>
  `

  css = () => `
    :host {
      display: block;
    }
  `

  eventListeners: EventListeners = []
}
